<template>
  <div>
    <!-- Display field showing count -->
    <v-text-field
      :value="displayValue"
      :label="label"
      :disabled="disabled"
      :hide-details="true"
      readonly
      @click="handleClick"
      class="cursor-pointer"
    >
      <template v-slot:append>
        <v-icon @click.stop="openDialog">mdi-pencil</v-icon>
        <v-icon
          v-if="value && value.length > 0"
          @click.stop="clearInput"
          class="ml-2"
          >mdi-close</v-icon
        >
      </template>
    </v-text-field>

    <!-- Dialog with textarea -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ dialogTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="textareaValue"
            :placeholder="placeholder"
            :label="label"
            rows="10"
            outlined
            auto-grow
          ></v-textarea>
          <div class="text-caption grey--text">
            Enter one value per line. Total: {{ itemCount }} items
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="error" @click="clearDialog">Clear</v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDialog">Cancel</v-btn>
          <v-btn color="primary" @click="applyDialog">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * XBatchTextInput - A component for batch text input via dialog
 *
 * Displays a text field that shows the count of selected items.
 * Clicking the field opens a dialog with a textarea where users can
 * enter multiple values (one per line).
 *
 * Usage:
 * <XBatchTextInput
 *   v-model="myArray"
 *   label="Order IDs"
 *   placeholder="Enter order IDs (one per line)"
 * />
 */
export default {
  name: "XBatchTextInput",

  props: {
    /**
     * The array of values (v-model binding)
     */
    value: {
      type: Array,
      default: () => [],
    },

    /**
     * Label for the input field
     */
    label: {
      type: String,
      default: "",
    },

    /**
     * Placeholder text for the textarea in the dialog
     */
    placeholder: {
      type: String,
      default: "Enter values (one per line)",
    },

    /**
     * Whether the input is disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dialog: false,
      textareaValue: "",
    };
  },

  computed: {
    /**
     * Display value showing the count of selected items
     */
    displayValue() {
      const count = this.value?.length || 0;
      return count > 0 ? `${count} items selected` : "";
    },

    /**
     * Dialog title based on the label
     */
    dialogTitle() {
      return this.label ? `Enter ${this.label}` : "Enter values";
    },

    /**
     * Count of items in the current textarea value
     */
    itemCount() {
      return this.stringToArray(this.textareaValue).length;
    },
  },

  methods: {
    /**
     * Convert array to newline-separated string
     */
    arrayToString(arr) {
      return arr ? arr.join("\n") : "";
    },

    /**
     * Convert newline-separated string to array
     * Trims whitespace and filters empty lines
     */
    stringToArray(str) {
      if (!str) {
        return [];
      }
      return str
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    },

    /**
     * Handle click on the text field
     */
    handleClick() {
      if (!this.disabled) {
        this.openDialog();
      }
    },

    /**
     * Open the dialog and initialize textarea value
     */
    openDialog() {
      if (!this.disabled) {
        this.textareaValue = this.arrayToString(this.value);
        this.dialog = true;
      }
    },

    /**
     * Apply the changes and emit the new value
     */
    applyDialog() {
      const newValue = this.stringToArray(this.textareaValue);
      this.$emit("input", newValue);
      this.dialog = false;
    },

    /**
     * Cancel the dialog without applying changes
     */
    cancelDialog() {
      this.dialog = false;
    },

    /**
     * Clear the input field value
     */
    clearInput() {
      if (!this.disabled) {
        this.$emit("input", []);
      }
    },

    /**
     * Clear the textarea in the dialog
     */
    clearDialog() {
      this.textareaValue = "";
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
